import { useMemo } from "react";

import { useTranslation } from "@/Common/CustomHooks/useTranslation";
import { DateHelper } from "@/Common/Helpers/DateHelper";

import styles from "./SnapshotDates.module.scss";

interface SnapshotDatesProps {
    dateReviewed: Date | null;
    lastReviewedDate: Date | null;
}

/**
 * Displays the snapshot dates for the care element.
 * @component
 * @param {SnapshotDatesProps} props - The properties for the component.
 * @property {Date | null} props.dateReviewed - The date when the care element was reviewed.
 * @property {Date | null} props.lastReviewedDate - The last date when the care element was reviewed.
 */
export function SnapshotDates({
    dateReviewed,
    lastReviewedDate
}: Readonly<SnapshotDatesProps>): JSX.Element {
    const { getText } = useTranslation();

    const dateReviewedValue = useMemo(() => DateHelper.formatDateLuxon(dateReviewed, "yyyy-MM-dd"), [dateReviewed]);
    const lastReviewedDateValue = useMemo(() => DateHelper.formatDateLuxon(lastReviewedDate, "yyyy-MM-dd"), [lastReviewedDate]);

    return (
        <div className={styles.snapshotDates}>
            <time className={styles.snapshotDateReviewed} dateTime={dateReviewedValue}>
                {dateReviewedValue}
            </time>
            {lastReviewedDate && (
                <p className={styles.snapshotLastReviewedDate}>
                    {`${getText("Flowsheets_CheckboxDateRowLabel_LastReviewed")}: `} <time dateTime={lastReviewedDateValue}>{lastReviewedDateValue}</time>
                </p>
            )}
        </div>
    );
}
